import * as React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { UpdateTenantSettingsSchema, type UpdateTenantSettingsInput } from '@cutlyft/zod-schemas';
import { useAuthStore } from '@/stores/auth.store';
import { useTenantMe } from '@/hooks/tenant/use-tenant-me';
import { useUpdateTenant } from '@/hooks/tenant/use-update-tenant';
import { useTenantLogoUpload } from '@/hooks/tenant/use-tenant-logo-upload';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Combobox } from '@/components/ui/combobox';
import { Separator } from '@/components/ui/separator';

export function TenantSettings() {
  const user = useAuthStore((state) => state.user);
  const { data, isLoading } = useTenantMe();
  const updateTenantMutation = useUpdateTenant();
  const uploadLogoMutation = useTenantLogoUpload();
  const { toast } = useToast();

  const isAuthorized = React.useMemo(() => {
    return user?.roleCode === 'OWNER' || user?.roleCode === 'ADMIN';
  }, [user]);

  const form = useForm<UpdateTenantSettingsInput>({
    resolver: zodResolver(UpdateTenantSettingsSchema),
    defaultValues: {
      name: '',
      kerfMm: 4,
      offcutThresholdMm: 1000,
      defaultStockBarMm: 6000,
      defaultUnitCode: 'MM',
      remarks: '',
    },
  });

  // Dynamically set form values once tenant details load
  React.useEffect(() => {
    if (data?.tenant) {
      form.reset({
        name: data.tenant.name,
        kerfMm: data.tenant.defaults.kerfMm,
        offcutThresholdMm: data.tenant.defaults.offcutThresholdMm,
        defaultStockBarMm: data.tenant.defaults.defaultStockBarMm,
        defaultUnitCode: data.tenant.defaults.unitCode,
      });
    }
  }, [data, form]);

  const onSubmit = (values: UpdateTenantSettingsInput) => {
    updateTenantMutation.mutate(values, {
      onSuccess: () => {
        toast({
          title: 'Settings Saved',
          description: 'Tenant settings have been updated successfully.',
        });
      },
      onError: (err: any) => {
        toast({
          variant: 'destructive',
          title: 'Save Failed',
          description: err.response?.data?.error?.message || 'Failed to save tenant settings.',
        });
      },
    });
  };

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Client-side file size and format check
    if (file.size > 2 * 1024 * 1024) {
      toast({
        variant: 'destructive',
        title: 'Upload Rejected',
        description: 'File size must be smaller than 2MB.',
      });
      return;
    }

    if (!['image/png', 'image/jpeg', 'image/jpg'].includes(file.type)) {
      toast({
        variant: 'destructive',
        title: 'Upload Rejected',
        description: 'Only PNG or JPG files are allowed.',
      });
      return;
    }

    uploadLogoMutation.mutate(
      { file },
      {
        onSuccess: () => {
          toast({
            title: 'Logo Uploaded',
            description: 'Company logo uploaded and updated successfully.',
          });
        },
        onError: (err: any) => {
          // Handle 503 Stub gracefully
          const status = err.response?.status;
          const apiMessage = err.response?.data?.error?.message;
          
          if (status === 503 || apiMessage?.includes('Logo upload pending')) {
            toast({
              variant: 'destructive',
              title: 'Feature Pending',
              description: 'Logo upload is currently pending AWS S3 storage integration.',
            });
          } else {
            toast({
              variant: 'destructive',
              title: 'Upload Failed',
              description: apiMessage || 'Failed to upload logo.',
            });
          }
        },
      }
    );
  };

  if (isLoading) {
    return (
      <Card className="border border-border/50 bg-bg-elevated/80 shadow-md">
        <CardHeader>
          <Skeleton className="h-6 w-48 mb-2" />
          <Skeleton className="h-4 w-72" />
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border border-border/50 bg-bg-elevated/80 shadow-md">
      <CardHeader>
        <CardTitle className="text-xl text-fg-primary">Workspace &amp; Fabrication Settings</CardTitle>
        <CardDescription className="text-fg-secondary">
          Configure default fabrication properties, saw kerfs, and branding logo.
        </CardDescription>
        {!isAuthorized && (
          <div className="p-3 mt-2 text-xs rounded bg-warning-bg text-warning border border-warning/20">
            ⚠️ You are in read-only mode. Only Workspace Owners and Administrators can modify these settings.
          </div>
        )}
      </CardHeader>
      <CardContent className="space-y-6">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Company / Fabricator Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter workspace name" {...field} disabled={!isAuthorized} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="defaultUnitCode"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Fabrication Unit</FormLabel>
                    <Combobox
                      options={[{ value: 'MM', label: 'Millimeters (mm)' }]}
                      value={field.value}
                      onValueChange={(v) => field.onChange(v ?? field.value)}
                      placeholder="Select unit"
                      searchPlaceholder="Search unit..."
                      disabled={!isAuthorized || field.disabled}
                      clearable={false}
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="kerfMm"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Saw Kerf (mm)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        placeholder="4"
                        {...field}
                        onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                        disabled={!isAuthorized}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="offcutThresholdMm"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Offcut Minimum Threshold (mm)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        placeholder="1000"
                        {...field}
                        onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                        disabled={!isAuthorized}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="defaultStockBarMm"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Default Stock Bar Length (mm)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        placeholder="6000"
                        {...field}
                        onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                        disabled={!isAuthorized}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {isAuthorized && (
              <div className="flex justify-end pt-2">
                <Button
                  type="submit"
                  className="bg-primary hover:bg-primary/95 text-primary-foreground font-semibold px-6"
                  disabled={updateTenantMutation.isPending}
                >
                  {updateTenantMutation.isPending ? 'Saving...' : 'Save Settings'}
                </Button>
              </div>
            )}
          </form>
        </Form>

        <Separator className="bg-border/50" />

        {/* Branding Logo Section */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-fg-primary">Company Branding Logo</h3>
          <div className="flex items-center gap-6">
            <div className="relative h-20 w-20 flex items-center justify-center rounded-lg border border-border bg-bg-secondary overflow-hidden">
              {data?.tenant?.hasLogo ? (
                <img
                  src={`${import.meta.env.VITE_API_URL}/tenant/me/logo-image?t=${Date.now()}`}
                  alt="Company Logo"
                  className="h-full w-full object-contain"
                />
              ) : (
                <span className="text-xs text-fg-muted font-semibold uppercase text-center px-1">No Logo</span>
              )}
            </div>
            {isAuthorized ? (
              <div className="space-y-2">
                <Label htmlFor="logo-file" className="cursor-pointer">
                  <div className="inline-flex items-center justify-center rounded-md text-sm font-semibold h-10 px-4 py-2 border border-border hover:bg-secondary text-fg-primary">
                    {uploadLogoMutation.isPending ? 'Uploading...' : 'Choose Logo Image'}
                  </div>
                </Label>
                <input
                  id="logo-file"
                  type="file"
                  accept="image/png, image/jpeg, image/jpg"
                  onChange={handleLogoUpload}
                  className="hidden"
                  disabled={uploadLogoMutation.isPending}
                />
                <p className="text-xs text-fg-muted">PNG or JPG formats supported. Max size 2MB.</p>
              </div>
            ) : (
              <p className="text-xs text-fg-muted">Branding logo can only be uploaded by administrators.</p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
